import React, { useEffect, useRef } from "react";
import { ChevronDown, ChevronRight, Play, Quiz } from "./icons";

/**
 * The section rail — the index of a section's modules and lessons.
 *
 * Previously a full-height coloured panel that carried its own Dashboard button
 * and Logout button; both now live in the app shell, so the rail is free to be
 * what it is: a quiet contents list. The active module is marked by tint rather
 * than by a heavier border, and lessons hang off a hairline so the hierarchy is
 * visible without indentation guesswork.
 *
 * The prop contract is unchanged — Home, Intranet and LnDTopics all mount this
 * and pass the same shape they always did. `isIntranet` keeps the intranet's
 * category-toggle behaviour (where clicking a category *is* the navigation)
 * distinct from the module workspace (where it opens a feedback prompt first).
 */
const Sidebar = ({
  categories = [],
  onItemClick,
  openCategory,
  setOpenCategory,
  selectedPPT,
  selectedPPTIndex,
  selectedSection,
  currentSection,
  isIntranet,
  handleSwitchModule,
}) => {
  const activeRef = useRef(null);

  useEffect(() => {
    if (openCategory !== null && activeRef.current) {
      const timer = setTimeout(() => {
        activeRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [openCategory]);

  const sectionName = localStorage.getItem("section") || "Section";

  return (
    <nav className="ds-card ds-rail">
      <div className="ds-rail__head">
        <p className="ds-eyebrow">
          {isIntranet ? "Intranet" : sectionName} — {isIntranet ? "Categories" : "Modules"}
        </p>
      </div>

      {categories.map((category, index) => {
        const isOpen = openCategory === index;
        return (
          <div key={category._id || category.title || index} ref={isOpen ? activeRef : null}>
            <button
              type="button"
              className={`ds-rail__item${isOpen ? " ds-rail__item--active" : ""}`}
              onClick={() => {
                if (isIntranet) {
                  setOpenCategory(isOpen ? null : index);
                  currentSection?.(category.title);
                } else {
                  handleSwitchModule?.(index, category.title);
                }
              }}
            >
              {isOpen ? (
                <ChevronDown size={15} />
              ) : (
                <ChevronRight size={15} />
              )}
              <span className="ds-rail__item-text" title={category.title}>
                {category.title}
              </span>
            </button>

            {isOpen && category.items?.length > 0 && (
              <ul className="ds-rail__lessons">
                {category.items.map((item, itemIndex) => {
                  const isActive =
                    selectedPPTIndex === itemIndex && selectedPPT?._id === item?._id;
                  return (
                    <li key={item._id || itemIndex}>
                      <button
                        type="button"
                        className={`ds-rail__lesson${isActive ? " ds-rail__lesson--active" : ""}`}
                        onClick={() => onItemClick?.(item, itemIndex)}
                      >
                        {isActive && <Play size={10} />}
                        <span className="ds-truncate" title={item.title}>
                          {item.title}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}

      {!isIntranet && (
        <div
          style={{
            marginTop: "var(--s-2)",
            paddingTop: "var(--s-2)",
            borderTop: "1px solid var(--border-subtle)",
          }}
        >
          <button
            type="button"
            className={`ds-rail__item${
              selectedSection === "generalWarmup" ? " ds-rail__item--active" : ""
            }`}
            onClick={() => {
              currentSection?.("generalWarmup");
              setOpenCategory?.(null);
            }}
          >
            <Quiz size={15} />
            <span className="ds-rail__item-text">Warm up</span>
          </button>
        </div>
      )}
    </nav>
  );
};

export default Sidebar;
